import React, { useEffect, useState } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { CheckCircle, UserPlus } from 'lucide-react';

const S = {
  heading: { fontSize:22, fontWeight:700, color:'#f6c90e', marginBottom:4 },
  sub: { fontSize:13, color:'#475569', marginBottom:28 },
  card: { background:'#0e1420', border:'1px solid #1a2035', borderRadius:14, padding:28, maxWidth:560 },
  label: { fontSize:11, color:'#64748b', textTransform:'uppercase', letterSpacing:1, marginBottom:5, display:'block' },
  input: { width:'100%', padding:'10px 12px', borderRadius:8, background:'#0f1117', border:'1px solid #1a2035', color:'#e2e8f0', fontSize:13, outline:'none', marginBottom:16, boxSizing:'border-box' },
  select: { width:'100%', padding:'10px 12px', borderRadius:8, background:'#0f1117', border:'1px solid #1a2035', color:'#e2e8f0', fontSize:13, outline:'none', marginBottom:16 },
  row2: { display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 },
  btn: (c='#f6c90e') => ({ padding:'11px 20px', borderRadius:8, border:`1px solid ${c}44`, background:`${c}22`, color:c, fontSize:14, fontWeight:700, cursor:'pointer' }),
  prazoBox: { background:'rgba(246,201,14,0.06)', border:'1px solid rgba(246,201,14,0.2)', borderRadius:10, padding:16, marginBottom:16 },
  success: { background:'rgba(74,222,128,0.1)', border:'1px solid rgba(74,222,128,0.3)', borderRadius:12, padding:24, textAlign:'center' },
  sectionTitle: { fontSize:11, color:'#2dd4bf', textTransform:'uppercase', letterSpacing:1.5, marginBottom:10, marginTop:4, fontWeight:700 },
  errorBox: { background:'rgba(248,113,113,0.1)', border:'1px solid rgba(248,113,113,0.3)', borderRadius:8, padding:'10px 14px', color:'#f87171', fontSize:13, marginBottom:16 },
};

const ESTADOS = [
  'AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG',
  'PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO',
];

const EMPTY = {
  nome:'', telefone:'', cpf_doc:'', rg_doc:'',
  endereco:'', cidade:'', estado:'',
  consumo_mensal:'', como_foi_conversa:'',
  prazo_resposta_vendedor:'',
  nota_vendedor:'',
};

export default function CadastrarCliente() {
  const { isChefe } = useAuth();
  const [form, setForm] = useState(EMPTY);
  const [vendedorId, setVendedorId] = useState('');
  const [vendedores, setVendedores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isChefe()) {
      api.get('/usuarios/').then(r => {
        setVendedores(r.data.filter(u => (u.perfil_acesso === 1 || u.perfil_acesso === 2 || u.perfil_acesso === 3) && u.ativo !== false));
      }).catch(() => {});
    }
  }, []);

  const f = k => e => setForm(p => ({ ...p, [k]: e.target.value }));

  const submit = async () => {
    if (!form.nome || !form.telefone) {
      setError('Nome e telefone são obrigatórios.');
      return;
    }
    if (isChefe() && !vendedorId) {
      setError('Selecione o vendedor responsável.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const payload = { ...form };
      if (isChefe() && vendedorId) payload.vendedor_id = vendedorId;
      const r = await api.post('/leads/vendedor-cadastrar/', payload);
      setSuccess(r.data);
      setForm(EMPTY);
      setVendedorId('');
    } catch (e) {
      setError('Erro ao cadastrar: ' + (e.response?.data ? JSON.stringify(e.response.data) : e.message));
    } finally {
      setLoading(false);
    }
  };

  if (success) return (
    <div>
      <div style={S.heading}>Cadastrar Cliente</div>
      <div style={S.sub}>{isChefe() ? 'Cadastre um cliente e designe o vendedor responsável' : 'Registre um novo cliente direto na sua carteira'}</div>
      <div style={{ ...S.success, maxWidth:480 }}>
        <CheckCircle size={40} color="#4ade80" style={{ margin:'0 auto 12px', display:'block' }} />
        <div style={{ fontSize:16, fontWeight:700, color:'#4ade80', marginBottom:8 }}>Cliente cadastrado com sucesso!</div>
        <div style={{ fontSize:13, color:'#94a3b8', marginBottom:16 }}>
          Cliente: <strong style={{ color:'#e2e8f0' }}>{success.cliente_nome}</strong>
          <br />Lead designado para: <strong style={{ color:'#f6c90e' }}>{success.vendedor_nome}</strong>
        </div>
        <button style={S.btn()} onClick={() => setSuccess(null)}>Cadastrar outro cliente</button>
      </div>
    </div>
  );

  return (
    <div>
      <div style={S.heading}>Cadastrar Cliente</div>
      <div style={S.sub}>{isChefe() ? 'Cadastre um cliente e designe o vendedor responsável' : 'Registre um novo cliente direto na sua carteira'}</div>

      <div style={S.card}>

        {/* Vendedor (somente chefe) */}
        {isChefe() && (
          <>
            <div style={S.sectionTitle}>Designar Vendedor</div>
            <label style={S.label}>Vendedor responsável *</label>
            <select style={S.select} value={vendedorId} onChange={e => setVendedorId(e.target.value)}>
              <option value="">Selecione o vendedor...</option>
              {vendedores.map(v => (
                <option key={v.id} value={v.id}>{v.nome || v.user?.first_name}</option>
              ))}
            </select>
          </>
        )}

        {/* Dados básicos */}
        <div style={S.sectionTitle}>Dados do Cliente</div>

        <label style={S.label}>Nome do cliente *</label>
        <input style={S.input} value={form.nome} onChange={f('nome')} placeholder="Nome completo" />

        <label style={S.label}>Telefone *</label>
        <input style={S.input} value={form.telefone} onChange={f('telefone')} placeholder="(34) 9-0000-0000" />

        <div style={S.row2}>
          <div>
            <label style={S.label}>CPF / CNPJ</label>
            <input style={S.input} value={form.cpf_doc} onChange={f('cpf_doc')} placeholder="000.000.000-00" />
          </div>
          <div>
            <label style={S.label}>RG</label>
            <input style={S.input} value={form.rg_doc} onChange={f('rg_doc')} placeholder="MG-00.000.000" />
          </div>
        </div>

        {/* Endereço */}
        <div style={S.sectionTitle}>Endereço</div>

        <label style={S.label}>Endereço (Rua, Número, Bairro)</label>
        <input style={S.input} value={form.endereco} onChange={f('endereco')} placeholder="Rua das Flores, 123, Centro" />

        <div style={S.row2}>
          <div>
            <label style={S.label}>Cidade</label>
            <input style={S.input} value={form.cidade} onChange={f('cidade')} placeholder="Uberlândia" />
          </div>
          <div>
            <label style={S.label}>Estado</label>
            <select style={S.select} value={form.estado} onChange={f('estado')}>
              <option value="">Selecione</option>
              {ESTADOS.map(uf => <option key={uf} value={uf}>{uf}</option>)}
            </select>
          </div>
        </div>

        {/* Informações */}
        <div style={S.sectionTitle}>Informações da Conversa</div>

        <label style={S.label}>Consumo mensal de energia (R$)</label>
        <input style={S.input} type="number" value={form.consumo_mensal} onChange={f('consumo_mensal')} placeholder="350.00" />

        <label style={S.label}>Como foi a conversa</label>
        <textarea
          style={{ ...S.input, height:90, resize:'vertical' }}
          value={form.como_foi_conversa}
          onChange={f('como_foi_conversa')}
          placeholder="Descreva como foi o contato inicial com o cliente..."
        />

        {/* Prazo de retorno + Nota */}
        <div style={S.prazoBox}>
          <label style={{ ...S.label, color:'#f6c90e' }}>Quando devo retornar para este cliente?</label>
          <input
            style={{ ...S.input, marginBottom:12, border:'1px solid rgba(246,201,14,0.3)' }}
            type="datetime-local"
            value={form.prazo_resposta_vendedor}
            onChange={f('prazo_resposta_vendedor')}
          />
          <label style={{ ...S.label, color:'#94a3b8', marginBottom:5 }}>Nota para o vendedor (opcional)</label>
          <textarea
            style={{ ...S.input, height:80, resize:'vertical', marginBottom:0, border:'1px solid rgba(246,201,14,0.15)' }}
            placeholder="Escreva uma nota para o vendedor responsável..."
            value={form.nota_vendedor}
            onChange={f('nota_vendedor')}
          />
          <div style={{ fontSize:11, color:'#64748b', marginTop:6 }}>
            Esta nota ficará registrada na aba Nota Vendedor.
          </div>
        </div>

        {error && <div style={S.errorBox}>{error}</div>}

        <div style={{ display:'flex', justifyContent:'flex-end', marginTop:8 }}>
          <button style={S.btn()} onClick={submit} disabled={loading}>
            {loading ? 'Cadastrando...' : 'Cadastrar Cliente'}
          </button>
        </div>
      </div>
    </div>
  );
}
